import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tab, Tabs, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Application.css";

function ViewApplicantsPage() {
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch applicants from backend
  useEffect(() => {
    const fetchApplicants = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "https://healthcare.visionkraftconsulting.com/api/applicants"
        );

        if (response.data && response.data.success && response.data.applications) {
          setApplicants(response.data.applications);
          setError("");
        } else {
          throw new Error("Invalid response structure");
        }
      } catch (err) {
        setError("Failed to load applicants. Please try again.");
        console.error("[ERROR] Fetch applicants failed:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchApplicants();
  }, []);

  // Render key-value pairs for objects
  const renderKeyValuePairs = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return (
        <div className="text-muted d-flex align-items-center">
          <i className="bi bi-info-circle me-2"></i>
          No data available
        </div>
      );
    }
    return (
      <div>
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} className="info-row">
            <span className="info-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
            <span className="info-value">{value || "N/A"}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render list of items for JSON array fields
  const renderList = (array, keys) => {
    if (!array || array.length === 0) {
      return (
        <div className="text-muted d-flex align-items-center">
          <i className="bi bi-info-circle me-2"></i>
          No data available
        </div>
      );
    }
    return (
      <ul className="item-list">
        {array.map((item, index) => (
          <li key={index}>
            {keys.map((key) => (
              <div key={key} className="info-row">
                <span className="info-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                <span className="info-value">{item[key] || "N/A"}</span>
              </div>
            ))}
          </li>
        ))}
      </ul>
    );
  };

  // Render file link with preview for uploaded files
// Render file link with a preview (PDFs or images)
const renderFileLinkWithPreview = (url, label) => {
  if (url) {
    const isImage = /\.(jpg|jpeg|png|gif)$/i.test(url);
    const isPDF = /\.pdf$/i.test(url);
    const getFileIcon = () => {
      if (isPDF) return "bi-file-earmark-pdf";
      if (isImage) return "bi-file-earmark-image";
      return "bi-file-earmark";
    };

    return (
      <div>
        <a href={url} target="_blank" rel="noopener noreferrer" className="file-link">
          <i className={`bi ${getFileIcon()}`}></i>
          View {label}
        </a>
        {isImage && (
          <div className="mt-3">
            <img
              src={url}
              alt={label}
              className="img-fluid"
              style={{
                maxWidth: "300px",
                maxHeight: "300px",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
              }}
            />
          </div>
        )}
        {isPDF && (
          <div className="mt-3">
            <embed
              src={url}
              type="application/pdf"
              style={{ 
                width: "100%", 
                height: window.innerWidth > 768 ? "500px" : "300px", 
                border: "1px solid #dee2e6",
                borderRadius: "8px"
              }}
            />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="text-muted d-flex align-items-center">
      <i className="bi bi-x-circle me-2"></i>
      Not Uploaded
    </div>
  );
};

  // Filter applicants based on search and status
  const filteredApplicants = applicants.filter(applicant => {
    const matchesSearch = applicant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         applicant.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || applicant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="applicants-container">
      {/* Header */}
      <div className="applicants-header">
        <h1><i className="bi bi-people-fill me-3"></i>Job Applicants</h1>
        <p>Review and manage healthcare caregiver applications</p>
      </div>

      {/* Search and Filter */}
      <div className="search-filter-section">
        <div className="search-box">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-tags">
          <button 
            className={`filter-tag ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All Applications
          </button>
          <button 
            className={`filter-tag ${statusFilter === 'new' ? 'active' : ''}`}
            onClick={() => setStatusFilter('new')}
          >
            New
          </button>
          <button 
            className={`filter-tag ${statusFilter === 'reviewed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('reviewed')}
          >
            Reviewed
          </button>
          <button 
            className={`filter-tag ${statusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => setStatusFilter('approved')}
          >
            Approved
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
          <h5>Loading applicants...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="alert-container">
          <div className="custom-alert alert-danger">
            <i className="bi bi-exclamation-circle"></i>
            {error}
          </div>
        </div>
      )}

      {/* Main Content */}
      {!loading && !error && (
        <div className="applicants-grid">
          {/* Applicants List */}
          <div className="applicants-list-section">
            <div className="applicants-list-header">
              <h4><i className="bi bi-list-ul me-2"></i>Applicants</h4>
              <span className="applicants-count">{filteredApplicants.length}</span>
            </div>
            
            {filteredApplicants.length > 0 ? (
              <div className="applicants-list">
                {filteredApplicants.map((applicant) => (
                  <div
                    key={applicant.id}
                    className={`applicant-item ${
                      selectedApplicant?.id === applicant.id ? "active" : ""
                    }`}
                    onClick={() => setSelectedApplicant(applicant)}
                  >
                    <div className="applicant-info">
                      <div className="applicant-name">
                        {applicant.name || "Unnamed Applicant"}
                      </div>
                      <div className="applicant-meta">
                        <span><i className="bi bi-envelope me-1"></i>{applicant.email || 'No email'}</span>
                        <span><i className="bi bi-telephone me-1"></i>{applicant.phone || 'No phone'}</span>
                      </div>
                    </div>
                    <div className={`applicant-status status-${applicant.status || 'new'}`}>
                      {applicant.status || 'New'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <i className="bi bi-inbox"></i>
                <h4>No applicants found</h4>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>

          {/* Applicant Details */}
          <div className="applicant-details-section">
            {selectedApplicant ? (
              <>
                <div className="applicant-details-header">
                  <h3>
                    <i className="bi bi-person-circle"></i>
                    {selectedApplicant.name || "Unnamed Applicant"}
                  </h3>
                </div>
                <div className="applicant-details-content">
                  <Tabs defaultActiveKey="personalInfo" className="custom-tabs">
                    {/* Personal Info Tab */}
                    <Tab eventKey="personalInfo" title={<><i className="bi bi-person me-2"></i>Personal Info</>}>
                      <div className="info-card">
                        <h5><i className="bi bi-person-circle"></i>Basic Information</h5>
                        <div className="info-row">
                          <span className="info-label">Full Name</span>
                          <span className="info-value">{selectedApplicant.name || "N/A"}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Email Address</span>
                          <span className="info-value">
                            <a href={`mailto:${selectedApplicant.email}`}>
                              {selectedApplicant.email || "N/A"}
                            </a>
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Phone Number</span>
                          <span className="info-value">
                            <a href={`tel:${selectedApplicant.phone}`}>
                              {selectedApplicant.phone || "N/A"}
                            </a>
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Mailing Address</span>
                          <span className="info-value">{selectedApplicant.address || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div className="info-card">
                        <h5><i className="bi bi-card-text"></i>License Information</h5>
                        <div className="info-row">
                          <span className="info-label">License Number</span>
                          <span className="info-value">{selectedApplicant.licenseNumber || "N/A"}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">License State</span>
                          <span className="info-value">{selectedApplicant.licenseState || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div className="info-card">
                        <h5><i className="bi bi-file-earmark-text"></i>Resume</h5>
                        <div className="file-preview">
                          {renderFileLinkWithPreview(selectedApplicant.files?.resume, "Resume")}
                        </div>
                      </div>
                    </Tab>

                    {/* Application Details Tab */}
                    <Tab eventKey="applicationDetails" title={<><i className="bi bi-file-text me-2"></i>Application Details</>}>
                      <div className="info-card">
                        <h5><i className="bi bi-file-earmark-pdf"></i>Application PDF</h5>
                        <div className="file-preview">
                          {renderFileLinkWithPreview(selectedApplicant.files?.pdf, "Application PDF")}
                        </div>
                      </div>
                      
                      <div className="info-card">
                        <h5><i className="bi bi-people"></i>References</h5>
                        {renderList(selectedApplicant.references, ["name", "title", "phone", "datesKnown"])}
                      </div>
                      
                      <div className="info-card">
                        <h5><i className="bi bi-briefcase"></i>Work Experience</h5>
                        {renderList(selectedApplicant.experience, ["organization", "contact", "phone", "dates", "contactAllowed"])}
                      </div>
                    </Tab>

                    {/* Availability Tab */}
                    <Tab eventKey="availability" title={<><i className="bi bi-calendar me-2"></i>Availability</>}>
                      <div className="info-card">
                        <h5><i className="bi bi-clock"></i>General Availability</h5>
                        <div className="info-row">
                          <span className="info-label">Available All Hours</span>
                          <span className="info-value">
                            <span className={`badge ${selectedApplicant.availability?.allHours ? 'bg-success' : 'bg-secondary'}`}>
                              {selectedApplicant.availability?.allHours ? "Yes" : "No"}
                            </span>
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Live-in Availability</span>
                          <span className="info-value">
                            <span className={`badge ${selectedApplicant.availability?.liveIn ? 'bg-success' : 'bg-secondary'}`}>
                              {selectedApplicant.availability?.liveIn ? "Yes" : "No"}
                            </span>
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Max Live-in Days</span>
                          <span className="info-value">{selectedApplicant.availability?.maxLiveInDays || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div className="info-card">
                        <h5><i className="bi bi-calendar-week"></i>Weekly Schedule</h5>
                        {renderKeyValuePairs(selectedApplicant.availability?.schedule)}
                      </div>
                    </Tab>

              {/* Education Tab */}
              <Tab eventKey="education" title="Education">
                <h4>Education</h4>
                {renderList(selectedApplicant.education, [
                  "name",
                  "location",
                  "major",
                  "graduate",
                  "endDate",
                  "graduated",
                ])}
              </Tab>

              {/* Additional Info Tab */}
              <Tab eventKey="additionalInfo" title="Additional Info">
                <h4>Additional Information</h4>
                <p>
                  <strong>Emergency Contacts:</strong>
                </p>
                {renderList(selectedApplicant.emergencyContacts, [
                  "name",
                  "relationship",
                  "phone",
                  "altPhone",
                ])}
                <p>
                  <strong>Criminal History:</strong>
                </p>
                {renderKeyValuePairs(selectedApplicant.criminalHistory)}
                <p>
                  <strong>Smoker Info:</strong>
                </p>
                {renderKeyValuePairs(selectedApplicant.smokerInfo)}
              </Tab>

              {/* Uploaded Documents Tab */}
              <Tab eventKey="files" title="Uploaded Documents">
              <h4>Uploaded Documents</h4>
              <p>
                <strong>Resume:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.resume, "Resume")}
              </p>
              <p>
                <strong>Application PDF:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.pdf, "Application PDF")}
              </p>
              <p>
                <strong>CPR Certificate:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.cpr, "CPR Certificate")}
              </p>
              <p>
                <strong>TB Test:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.tb, "TB Test")}
              </p>
              <p>
                <strong>License:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.license, "License")}
              </p>
              <p>
                <strong>SSN:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.ssn, "SSN")}
              </p>
              <p>
                <strong>Driver’s License:</strong>{" "}
                {renderFileLinkWithPreview(selectedApplicant.files?.dl, "Driver’s License")}
              </p>
                    </Tab>
                  </Tabs>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <i className="bi bi-person-plus"></i>
                <h4>Select an Applicant</h4>
                <p>Choose an applicant from the list to view their details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewApplicantsPage;